import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard, { CategoryBadge } from "@/components/feature/ArticleCard";
import { getArticle, articles, type ContentBlock } from "@/mocks/articles";
import { getCategory } from "@/mocks/categories";
import { scrollToSubscribe } from "@/utils/subscribeNav";

const renderBlock = (block: ContentBlock, index: number) => {
  if (block.type === "h3") {
    return (
      <h3 key={index} className="mt-8 font-heading text-xl font-bold text-foreground-950">
        {block.text}
      </h3>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote
        key={index}
        className="my-6 border-l-4 border-accent-500 bg-accent-50 px-5 py-4 font-heading text-base font-medium italic text-foreground-800"
      >
        {block.text}
      </blockquote>
    );
  }
  return (
    <p key={index} className="mt-4 text-base leading-relaxed text-foreground-700">
      {block.text}
    </p>
  );
};

export default function ArticlePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const article = getArticle(id);

  if (!article) {
    return (
      <SiteLayout>
        <Seo
          title="内容未找到 - 爆料在线"
          description="这篇爆料可能已被更正或移除，返回首页可以继续浏览最新的独家内容。"
          keywords="爆料在线,内容未找到,独家猛料"
          canonical={`https://mtx360.cn/article/${id}`}
        />
        <div className="mx-auto w-full max-w-shell px-4 py-20 text-center md:px-6">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-file-search-line text-2xl" aria-hidden="true" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">没有找到这篇内容</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已被更正、合并或下线。</p>
          <Link
            to="/"
            className="mt-8 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = getCategory(article.category);
  const related = articles
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 3);
  const fallbackRelated = articles.filter((item) => item.id !== article.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        dateModified: article.date,
        articleSection: category?.name,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "爆料在线" },
        keywords: article.tags.join(","),
        url: `https://mtx360.cn/article/${article.id}`,
        inLanguage: "zh-CN",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://mtx360.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: category?.name ?? "内容",
            item: `https://mtx360.cn/category/${article.category}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.title,
            item: `https://mtx360.cn/article/${article.id}`,
          },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <Seo
        title={`${article.title} - 爆料在线`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonical={`https://mtx360.cn/article/${article.id}`}
        type="article"
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-shell px-4 py-10 md:px-6">
        <Breadcrumb
          items={[
            { label: category?.name ?? "内容", to: `/category/${article.category}` },
            { label: "正文" },
          ]}
        />

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <CategoryBadge slug={article.category} />
              <span className="text-xs text-secondary-600">{article.date}</span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 border-l-4 border-primary-500 pl-4 text-base leading-relaxed text-foreground-700">
              {article.excerpt}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-5 border-y border-background-200 py-4 text-xs text-secondary-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true" />
                文 / {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" aria-hidden="true" />
                {article.views} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true" />
                约 {article.readMinutes} 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-accent-600" aria-hidden="true" />
                信息已交叉核实
              </span>
            </div>

            <figure className="mt-7">
              <div className="h-[240px] w-full overflow-hidden rounded-lg md:h-[420px]">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - 爆料在线`}
                  className="h-full w-full object-top object-cover"
                />
              </div>
              <figcaption className="mt-2 text-xs text-secondary-600">
                配图 / 爆料在线编辑部 · 与本文人物无直接对应关系
              </figcaption>
            </figure>

            <div className="mt-8">
              {article.content.map((block, index) => renderBlock(block, index))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-sm font-medium text-foreground-800">相关标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="text-sm leading-relaxed text-foreground-600">
                本文内容基于公开信息与多方信源交叉核实，旨在提供信息参考。如你有更准确的信息或希望提出更正，欢迎发送邮件至
                <a href="mailto:contact@baoliao-online.example.com" className="ml-1 cursor-pointer text-primary-600 hover:underline">
                  contact@baoliao-online.example.com
                </a>
                。
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-links-line text-primary-600" aria-hidden="true" />
                相关阅读
              </h2>
              <div className="flex flex-col gap-5">
                {relatedList.map((item) => (
                  <ArticleCard key={item.id} article={item} variant="wide" />
                ))}
              </div>

              <div className="mt-8 rounded-lg bg-foreground-950 p-5 text-background-100">
                <h3 className="font-heading text-base font-bold text-background-50">不想错过下一条猛料？</h3>
                <p className="mt-2 text-xs leading-relaxed text-background-200/80">
                  订阅独家推送，我们只在你值得知道的时候出现。
                </p>
                <a
                  href="#subscribe"
                  onClick={(event) => {
                    event.preventDefault();
                    scrollToSubscribe(navigate);
                  }}
                  className="mt-4 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 hover:bg-accent-400"
                >
                  <i className="ri-mail-add-line" aria-hidden="true" />
                  去订阅
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}